import {
  WhmAutocomplete,
  WhmLoadingButton,
  WhmStack,
  WhmTextField,
  WhmTypography,
} from "@securitize/reactjs-whm";

import { ReactComponent as EmptyIllustation } from "./Empty.svg";
import { Translate } from "../../Translation/Translate";
import { I18nContext, translationKeys } from "../../../contexts/I18nContext";
import { useContext } from "react";
import { allowedEntityTypes } from "../information/entity/entityTypeList";
import { Form, Formik } from "formik";
import { entityTypesTranslations } from "../../../contexts/I18nContext/translationKeys";
import { updateInvestor } from "../../../data/api";
import {
  InvestorEntityType,
  InvestorEntityTypes,
} from "@securitize/domain/dist/sid";
import { SnackbarContext } from "../../shared/Snackbar";
import ErrorSnackbar from "../../shared/ErrorSnackbar";
import { ResponseError } from "../../../data/types/responseError";
import * as yup from "yup";

export default function MissingEntityType() {
  const snackbar = useContext(SnackbarContext);

  const { getTranslation } = useContext(I18nContext);

  const entityOptions = Object.values(allowedEntityTypes).map((e) => ({
    value: e,
    label: getTranslation(entityTypesTranslations[e]),
  }));

  return (
    <Formik
      validationSchema={yup.object().shape({
        entityType: yup
          .string()
          .oneOf(Object.values(InvestorEntityTypes))
          .required(
            getTranslation(translationKeys.MISSING_ENTITY_TYPE_REQUIRED),
          ),
      })}
      initialValues={{
        entityType: "",
      }}
      onSubmit={async (values, helpers) => {
        helpers.setSubmitting(true);

        try {
          await updateInvestor({
            details: {
              entityType: values.entityType as InvestorEntityType,
            },
          });
          window.location.reload();
        } catch (e) {
          snackbar.showSnackbar({
            message: <ErrorSnackbar error={e as ResponseError} />,
            severity: "error",
          });
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {(formik) => (
        <Form>
          <WhmStack
            alignItems="center"
            padding={{ xs: "16px", xl: "24px" }}
            gap="16px"
            justifyContent="center"
            height="100%"
          >
            <EmptyIllustation />
            <WhmStack alignContent="center" gap="8px">
              <WhmTypography variant="h4" align="center">
                <Translate
                  translationKey={translationKeys.MISSING_ENTITY_TYPE_TITLE}
                />
              </WhmTypography>
              <WhmTypography align="center">
                <Translate
                  translationKey={translationKeys.MISSING_ENTITY_TYPE_SUBTITLE}
                />
              </WhmTypography>
            </WhmStack>
            <WhmStack alignItems="center" gap="24px" width="100%">
              <WhmAutocomplete
                fullWidth
                sx={{ maxWidth: "400px" }}
                value={
                  entityOptions.find(
                    (o) => o.value === formik.values.entityType,
                  ) ?? null
                }
                onChange={(_, v) =>
                  void formik.setFieldValue("entityType", v?.value ?? "")
                }
                options={entityOptions}
                renderInput={(props) => (
                  <WhmTextField
                    {...props}
                    InputProps={{
                      // eslint-disable-next-line react/prop-types -- React types issue
                      ...props.InputProps,
                      sx: { backgroundColor: "white" },
                    }}
                    data-test-id="entity-type-input"
                    name="entityType"
                    error={
                      formik.touched.entityType &&
                      Boolean(formik.errors.entityType)
                    }
                    onBlur={formik.handleBlur}
                    helperText={
                      formik.touched.entityType && formik.errors.entityType
                    }
                    placeholder={getTranslation(
                      translationKeys.MISSING_ENTITY_TYPE_PLACEHOLDER,
                    )}
                    label={
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_ENTITY_TYPE
                        }
                      />
                    }
                  />
                )}
              />
              <WhmLoadingButton
                variant="contained"
                data-test-id="missing-entity-type-update-button"
                sx={{
                  width: { xs: "100%", lg: "fit-content" },
                  svg: {
                    display: "block !important",
                  },
                  "&.MuiLoadingButton-loading": {
                    color: "black",
                    opacity: "0.3",
                    span: {
                      color: "black",
                    },
                  },
                }}
                type="submit"
                loading={formik.isSubmitting}
              >
                {formik.isSubmitting ? (
                  <Translate
                    translationKey={
                      translationKeys.MISSING_ENTITY_TYPE_BUTTON_UPDATING
                    }
                  />
                ) : (
                  <Translate
                    translationKey={
                      translationKeys.MISSING_ENTITY_TYPE_BUTTON_UPDATE
                    }
                  />
                )}
              </WhmLoadingButton>
            </WhmStack>
          </WhmStack>
        </Form>
      )}
    </Formik>
  );
}
